import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import $ from 'jquery';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useState,
  useRef,
} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ConnectionFileStatus,
  getConnectionFileStatus,
} from 'instruments/connection-files/ConnectionFilesStatus';
import ConnectionFilesTable, {
  getFiltersFromConnectionIds,
  useConnectionIdsAndFiltersState,
} from 'instruments/connection-files/ConnectionFilesTable';
import {
  connectionRequiresUserLogin,
  ConnectionStatus,
  InstrumentServiceDetails,
} from 'instruments/connections/ConnectionConstants';
import * as ConnectionDAO from 'instruments/connections/ConnectionDAO';
import {extractFileNames} from 'instruments/instrument-data/BrowseConnectionFilesModal';
import importInstrumentDataModalReducer, {
  ImportInstrumentDataModalActionType,
} from 'instruments/instrument-data/importInstrumentDataModalReducer';
import {
  BROWSE_FILES_ADAPTERS,
  INITIAL_STATE,
  InstrumentData,
  InstrumentDataFileType,
  InstrumentQueryStatus,
  isUnselectableFileStatus,
  StepKey,
} from 'instruments/instrument-data/InstrumentDataConstants';
import {
  createBrowseFilesInstrumentQuery,
  pollInstrumentQueryStatus,
  pollUntil,
} from 'instruments/instrument-data/InstrumentDataDAO';
import InstrumentDataTable from 'instruments/instrument-data/InstrumentDataTable';
import InstrumentLoginPage from 'instruments/instrument-data/InstrumentLoginPage';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT,
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT,
} from 'performance/processLabAutomationEvents';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import SearchInput from 'search/inputs/SearchInput';
import {Filter, FilterKey, Operator, SearchType, SortKey} from 'search/SearchConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FilesPickerComponent from 'ui-library/FilesPickerComponent';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import * as logging from 'util/Logging';
import removeNulls from 'util/removeNulls';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {ConnectionListingGraphQL} from '../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';

interface SelectPipelineFilesModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  title: string;
  fileIds: Array<string>;
  isMulti: boolean;
  minItems?: number;
  maxItems?: number;
  onSelectFiles: (newFileIds: Array<string>) => void;
  onUploadFiles: (files: Array<File>, selectedFileIds?: Array<string>) => JQueryPromise<void>;
  defaultFilters?: Array<Filter>;
  runId: string;
}

interface ConnectionFilesTabProps extends SelectPipelineFilesModalProps {
  selectedFileIds: Array<string>;
  setSelectedFileIds: (selectedFileIds: Array<string>) => void;
}

enum TabType {
  DATA_CATALOG = 'data_catalog',
  UPLOAD = 'upload',
  CONNECTION = 'connection',
  RETRIEVE = 'retrieve',
}

function getInitiallySelectedRowIndices(
  connectionFiles: Array<FileListingGraphQL>,
  selectedFileIds: Array<string>
): Set<number> {
  return new Set(
    removeNulls(connectionFiles.map((file, idx) => (selectedFileIds.includes(file.id) ? idx : null)))
  );
}

function ConnectionFilesTab({
  isMulti,
  maxItems,
  selectedFileIds,
  setSelectedFileIds,
  defaultFilters,
}: ConnectionFilesTabProps): JSX.Element {
  const {
    connectionIdsState: [selectedConnectionIds, setSelectedConnectionIds],
    filtersState: [filters, setFilters], // State management for ConnectionFilesTable
  } = useConnectionIdsAndFiltersState({
    initialFilters: defaultFilters ?? getFiltersFromConnectionIds([]),
    initialConnectionIds: null,
  });
  // State management for ConnectionFilesTable
  const [connectionFiles, setConnectionFiles] = useState<Array<FileListingGraphQL>>([]);
  const [selectedRowIndices, setSelectedRowIndices] = useState<Set<number>>(
    getInitiallySelectedRowIndices(connectionFiles, selectedFileIds)
  );

  // Ensure that files that have already been selected and are present in the ConnectionFilesTable are
  // selected when the table loads.
  useEffect(() => {
    setSelectedRowIndices(getInitiallySelectedRowIndices(connectionFiles, selectedFileIds));
  }, [connectionFiles, selectedFileIds]);

  const hasMaxItems = maxItems != null && selectedFileIds.length >= maxItems;

  // Disable "select all" if clicking it would select more connection files than maxItems. Clicking on the
  // checkbox only "selects" if no row indices are selected yet. Otherwise, clicking on it gets rid of all
  // current selections.
  const isSelectAllDisabled =
    selectedRowIndices.size === 0 &&
    maxItems != null &&
    selectedFileIds.length + connectionFiles.length > maxItems;
  // If hasMaxItems === true, then only allow the user to de-select by disabling all *unselected* rows
  const isDisabledMap: Array<boolean> | undefined = hasMaxItems
    ? connectionFiles.map((file, rowIndex) => !selectedRowIndices.has(rowIndex))
    : undefined;

  function handleSetRowIndices(newIndices: Set<number>): void {
    // Set the selection state of the ConnectionFilesTable
    const newSelectedRowIndices = new Set(selectedRowIndices);
    for (const rowIndex of newIndices) {
      if (newSelectedRowIndices.has(rowIndex)) {
        // Selected--> unselected
        newSelectedRowIndices.delete(rowIndex);
      } else {
        // Unselected --> selected
        newSelectedRowIndices.add(rowIndex);
      }
    }
    setSelectedRowIndices(newSelectedRowIndices);

    const newSelectedConnectionFileIds = [...newSelectedRowIndices].map(
      (rowIndex) => connectionFiles[rowIndex].id
    );
    if (isMulti) {
      // Inspect selectedFileIds for ids that should not be affected by the change in row indices. Specifically,
      // we don't want to remove files in selectedFileIds that aren't configurable via ConnectionFilesTable,
      // and we also don't want to needlessly change the order of selectedFileIds.
      const allConnectionFileIds = connectionFiles.map(({id}) => id);
      const stableSelectedFileIds = selectedFileIds.filter(
        (id) => !allConnectionFileIds.includes(id) || newSelectedConnectionFileIds.includes(id)
      );
      setSelectedFileIds([
        ...stableSelectedFileIds,
        ...newSelectedConnectionFileIds.filter((id) => !stableSelectedFileIds.includes(id)),
      ]);
    } else {
      assert(
        newSelectedConnectionFileIds.length <= 1,
        'User was able to select multiple connection files for an isMulti=false file variable.'
      );
      setSelectedFileIds(newSelectedConnectionFileIds);
    }
  }

  return (
    <div className={css(styles.connectionTabBody)}>
      <div>
        <FieldLabel label="Connections to filter by" />
        <SearchInput
          autoFocus={false}
          types={[ModelTypeConstants.CONNECTION]}
          selected={selectedConnectionIds}
          onSelect={(connectionId) => {
            if (connectionId == null) {
              return;
            }
            setSelectedConnectionIds([...selectedConnectionIds, connectionId]);
          }}
          onRemove={(connectionId) => {
            if (connectionId == null) {
              return;
            }
            setSelectedConnectionIds(selectedConnectionIds.filter((id) => id !== connectionId));
          }}
          placeholder="Search for connections..."
          isMulti={isMulti}
        />
      </div>
      <div>
        <FieldLabel label="Connection files" />
        <ConnectionFilesTable
          isSelectAllDisabled={isSelectAllDisabled}
          isDisabledMap={isDisabledMap}
          hideCheckboxColumn={false}
          allowMultiple={isMulti}
          connectionFiles={connectionFiles}
          setConnectionFiles={setConnectionFiles}
          columns={['File name', 'Status', 'Modified at', 'Created at']}
          filters={filters}
          setFilters={setFilters}
          selectedRowIndices={selectedRowIndices}
          onSelectRows={handleSetRowIndices}
        />
      </div>
    </div>
  );
}

function searchForFile(filePath: string, connectionId: string): Promise<FileListingGraphQL> {
  // The COPY_OBJECT response may return a full path (e.g. chrom://host/path/file.seq).
  // The data catalog indexes files by basename only.
  const basename = filePath.split('/').pop() ?? filePath;
  return pollUntil(
    async () => {
      const filters = getFiltersFromConnectionIds([connectionId]);
      filters.push({
        key: FilterKey.NAMES,
        operator: Operator.IS_ONE_OF,
        value: [basename],
      });
      return (await GlobalSearchActions.doGlobalSearch(
        {
          offset: 0,
          limit: 1,
          sorts: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
          filters,
          query: '',
        },
        SearchType.GLOBAL
      )) as GlobalSearchResponse;
    },
    (response: GlobalSearchResponse) => response.ids.length === 1,
    3 * 1000, // 3 seconds between retries
    30 * 1000 // 30 seconds max wait
  ).then((result) => {
    if (result.ids.length !== 1) {
      throw new Error('Could not find the retrieved file in the data catalog.');
    }
    return result.filesById[result.ids[0]];
  });
}

interface BrowseFilesFlowHandle {
  submit: () => Promise<Array<string>>;
  isSubmitting: boolean;
}

interface BrowseFilesFlowProps {
  connectionId: string;
  instrumentServiceDetails: InstrumentServiceDetails;
  runId: string;
  isMulti: boolean;
}

const BrowseFilesFlow = forwardRef<BrowseFilesFlowHandle, BrowseFilesFlowProps>(
  ({connectionId, instrumentServiceDetails, runId, isMulti}, ref) => {
    const [state, dispatch] = useReducer(importInstrumentDataModalReducer, INITIAL_STATE);

    const [connectionFiles, setConnectionFiles] = useState<Array<FileListingGraphQL>>([]);
    const [selectedConnectionFileIndices, setSelectedConnectionFileIndices] = useState<Set<number>>(
      new Set()
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isBrowsing, setIsBrowsing] = useState(false);

    const isConnectionAvailable = instrumentServiceDetails.status.value === ConnectionStatus.AVAILABLE;

    // Skip login step if not required, but still send BROWSE_FILES to refresh file listing
    useEffect(() => {
      if (!connectionRequiresUserLogin(instrumentServiceDetails)) {
        let cancelled = false;
        setIsBrowsing(true);
        createBrowseFilesInstrumentQuery(connectionId)
          .then(({instrumentQueryId}) => pollInstrumentQueryStatus(instrumentQueryId))
          .then((query) => {
            if (cancelled) {
              return;
            }
            if (query.status === InstrumentQueryStatus.DONE) {
              dispatch({
                type: ImportInstrumentDataModalActionType.LOGIN,
                loginQueryStatus: query.status,
                loginUsername: '',
              });
            } else {
              const detail = query.info || 'An unknown error occurred';
              notifications.error(`Failed to retrieve file listing from connection: "${detail}"`);
            }
          })
          .catch((error: unknown) => {
            if (cancelled) {
              return;
            }
            const isTimeout = error instanceof Error && error.message.includes('Polling failed');
            if (isTimeout) {
              notifications.error('File listing request timed out. Please try again later.');
              return;
            }
            // jQuery XHR rejections include responseJSON with server error details
            const serverMessage = (error as {responseJSON?: {userMessage?: string}})?.responseJSON
              ?.userMessage;
            if (serverMessage) {
              notifications.error(`Failed to retrieve file listing from connection: "${serverMessage}"`);
              return;
            }
            notifications.error(
              'Failed to retrieve file listing from connection. Please contact Benchling support if the issue persists.'
            );
          })
          .finally(() => {
            if (cancelled) {
              return;
            }
            setIsBrowsing(false);
            dispatch({
              type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
              nextStepKey: StepKey.PROCESS,
            });
          });
        return () => {
          cancelled = true;
        };
      }
      return undefined;
    }, [instrumentServiceDetails, connectionId]);

    // Auto-advance after successful login
    useEffect(() => {
      if (state.currentStepKey === StepKey.LOGIN && state.loginQueryStatus === InstrumentQueryStatus.DONE) {
        dispatch({
          type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
          nextStepKey: StepKey.PROCESS,
        });
      }
    }, [state.loginQueryStatus, state.currentStepKey]);

    const {
      filtersState: [filters, setFilters],
    } = useConnectionIdsAndFiltersState({
      initialConnectionIds: [connectionId],
      initialFilters: null,
    });

    const setInstrumentDataCallback = useCallback(
      (instrumentData: Array<InstrumentData>): void => {
        dispatch({type: ImportInstrumentDataModalActionType.SET_INSTRUMENT_DATA, instrumentData});
      },
      [dispatch]
    );

    const setConnectionFilesCallback = useCallback(
      (files: Array<FileListingGraphQL>): void => {
        setConnectionFiles(files);
        setSelectedConnectionFileIndices(new Set());
      },
      [setConnectionFiles]
    );

    const toggleConnectionFileSelection = useCallback((rowIndex: number): void => {
      setSelectedConnectionFileIndices((prev) => {
        const next = new Set(prev);
        if (next.has(rowIndex)) {
          next.delete(rowIndex);
        } else {
          next.add(rowIndex);
        }
        return next;
      });
    }, []);

    // Submit: COPY_OBJECT selected instrument files, then return all selected file IDs
    useImperativeHandle(
      ref,
      () => ({
        isSubmitting,
        submit: async (): Promise<Array<string>> => {
          const selectedConnectionFileIds = [...selectedConnectionFileIndices].map(
            (index) => connectionFiles[index].id
          );

          let retrievedFileIds: Array<string> = [];
          if (state.selectedRowIndices.size > 0) {
            setIsSubmitting(true);
            try {
              logProfilerTimingEvent(LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT);
              // Don't pass runId: the server's send_instrument_query endpoint expects a
              // warehouse_id but SelectPipelineFilesModal receives an apiId. The file_id param
              // is sufficient for COPY_OBJECT in the browse-files flow.
              const fileNames = await extractFileNames(state);
              logProfilerTimingEvent(LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT);

              const files = await Promise.all(
                fileNames.map((fileName) => searchForFile(fileName, connectionId))
              );
              retrievedFileIds = files.map((file) => file.id);
            } finally {
              setIsSubmitting(false);
            }
          }

          // Combine: connection file selections + newly retrieved instrument files
          const allIds = [...selectedConnectionFileIds];
          for (const id of retrievedFileIds) {
            if (!allIds.includes(id)) {
              allIds.push(id);
            }
          }
          return allIds;
        },
      }),
      [isSubmitting, selectedConnectionFileIndices, connectionFiles, state, connectionId]
    );

    if (state.currentStepKey === StepKey.LOGIN) {
      if (isBrowsing) {
        return (
          <div className={css(styles.browseFilesFlow)}>
            <CenteredHorizontalThrobber />
          </div>
        );
      }
      return (
        <div className={css(styles.browseFilesFlow)}>
          <InstrumentLoginPage connectionId={connectionId} dispatch={dispatch} state={state} />
        </div>
      );
    }

    const totalSelectedCount = state.selectedRowIndices.size + selectedConnectionFileIndices.size;

    return (
      <div className={css(styles.browseFilesFlow)}>
        {!isConnectionAvailable && (
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={
              instrumentServiceDetails.status.value === ConnectionStatus.DISABLED
                ? 'This connection is disabled. Please re-enable it to retrieve the latest data.'
                : 'This connection is not currently available. Please try again later for the latest data.'
            }
          />
        )}

        <h4 className={css(styles.sectionHeader)}>Files on connection</h4>
        <InstrumentDataTable
          instrumentData={state.instrumentData}
          setInstrumentData={setInstrumentDataCallback}
          selectedRowIndices={state.selectedRowIndices}
          onSelectRow={(rowIndex: number): void => {
            if (
              !isUnselectableFileStatus(state.instrumentData[rowIndex].status, isConnectionAvailable) &&
              (isMulti || totalSelectedCount === 0 || state.selectedRowIndices.has(rowIndex))
            ) {
              dispatch({
                type: ImportInstrumentDataModalActionType.SELECT_ROW,
                selectedRowIndex: rowIndex,
              });
            }
          }}
          allowMultiple={isMulti}
          columns={['File name', 'Status', 'Modified at', 'Refetch']}
          fileTypeFilter={InstrumentDataFileType.ORIGINAL}
          filterOptions={[]}
          initialFilterValues={{[FilterKey.CONNECTION]: [connectionId]}}
          isImporting={isSubmitting}
          isConnectionAvailable={isConnectionAvailable}
          loginUsername={state.loginUsername}
        />

        <h4 className={css(styles.sectionHeader)}>Previously retrieved files</h4>
        <ConnectionFilesTable
          filters={filters}
          setFilters={setFilters}
          connectionFiles={connectionFiles}
          setConnectionFiles={setConnectionFilesCallback}
          selectedRowIndices={selectedConnectionFileIndices}
          onSelectRows={(rowIndices: Set<number>): void => {
            for (const rowIndex of rowIndices) {
              if (
                getConnectionFileStatus(connectionFiles[rowIndex], true) === ConnectionFileStatus.UPLOADED &&
                (isMulti || totalSelectedCount === 0 || selectedConnectionFileIndices.has(rowIndex))
              ) {
                toggleConnectionFileSelection(rowIndex);
              }
            }
          }}
          allowMultiple={isMulti}
          columns={['File name', 'Status', 'Modified at', 'Created at']}
        />
      </div>
    );
  }
);

interface RetrieveFromConnectionTabProps {
  runId: string;
  isMulti: boolean;
  browseFlowRef: React.RefObject<BrowseFilesFlowHandle>;
}

function RetrieveFromConnectionTab({
  runId,
  isMulti,
  browseFlowRef,
}: RetrieveFromConnectionTabProps): JSX.Element {
  const [connections, setConnections] = useState<Array<ConnectionListingGraphQL>>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState<InstrumentServiceDetails | null>(
    null
  );
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const loadConnections = useCallback(async () => {
    setIsLoadingConnections(true);
    const results = await Promise.allSettled(
      BROWSE_FILES_ADAPTERS.map((adapterId) => ConnectionDAO.listConnections(undefined, adapterId))
    );
    const hasFailures = results.some((r) => r.status === 'rejected');
    const allConnections = results.flatMap((result) =>
      result.status === 'fulfilled' ? result.value.connections : []
    );
    if (hasFailures && allConnections.length === 0) {
      notifications.error('Failed to load connections.');
    }
    setConnections(allConnections.filter((c) => c.archiveRecord == null));
    setIsLoadingConnections(false);
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // Fetch instrument service details when a connection is selected
  useEffect(() => {
    setInstrumentServiceDetails(null);
    if (!selectedConnectionId) {
      return;
    }
    setIsLoadingDetails(true);
    ConnectionDAO.getInstrumentServiceDetails(selectedConnectionId)
      .then(setInstrumentServiceDetails)
      .catch(() => {
        notifications.error('Unable to load connection details. Please try again.');
      })
      .finally(() => {
        setIsLoadingDetails(false);
      });
  }, [selectedConnectionId]);

  const selectedConnection = connections.find((c) => c.id === selectedConnectionId);

  const connectionOptionDatas = useMemo(
    () =>
      connections.map((connection) => ({
        value: connection.id,
        name: connection.name,
      })),
    [connections]
  );

  const renderFlow = (): JSX.Element | null => {
    if (!selectedConnectionId) {
      return null;
    }
    if (isLoadingDetails) {
      return <CenteredHorizontalThrobber />;
    }
    if (!instrumentServiceDetails) {
      return null;
    }

    return (
      <BrowseFilesFlow
        key={selectedConnectionId}
        ref={browseFlowRef}
        connectionId={selectedConnectionId}
        instrumentServiceDetails={instrumentServiceDetails}
        runId={runId}
        isMulti={isMulti}
      />
    );
  };

  return (
    <div className={css(styles.connectionTabBody)}>
      <div>
        <FieldLabel label="Connection" />
        {isLoadingConnections ? (
          <CenteredHorizontalThrobber />
        ) : connections.length === 0 ? (
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="No connections are available for data retrieval."
          />
        ) : (
          <SelectDropdown
            displayName={selectedConnection?.name ?? 'Select a connection...'}
            closeOnInsideClick={true}
            menu={
              <SearchableMenu
                width={Menu.WIDTHS.FULL}
                height={Menu.HEIGHTS.MEDIUM}
                onSelect={(value) => {
                  setSelectedConnectionId(value as string);
                }}
                optionDatas={connectionOptionDatas}
                selectedValue={selectedConnectionId ?? undefined}
              />
            }
          />
        )}
      </div>
      {renderFlow()}
    </div>
  );
}

export default function SelectPipelineFilesModal(props: SelectPipelineFilesModalProps): JSX.Element {
  const {title, fileIds, onSelectFiles, onRequestClose, isMulti, maxItems, onUploadFiles, runId} = props;
  const [activeTab, setActiveTab] = useState<TabType>(TabType.CONNECTION);
  const [selectedFileIds, setSelectedFileIds] = useState<Array<string>>(fileIds);
  const [isRetrieveSubmitting, setIsRetrieveSubmitting] = useState(false);

  const fileInputRef = useRef<FilesPickerComponent>(null);
  const browseFlowRef = useRef<BrowseFilesFlowHandle>(null);

  const handleSubmit = (): void => {
    if (activeTab === TabType.UPLOAD) {
      assert(fileInputRef.current != null, 'Expected non-null fileInputRef.current');
      fileInputRef.current.handleUpload({allComplete: onRequestClose, uploadComplete: noop});
    } else {
      onSelectFiles(selectedFileIds);
      onRequestClose();
    }
  };

  // Separate handler for RETRIEVE tab — uses onClick instead of form submit so the async
  // flow isn't silently dropped by Modal's synchronous onRequestSubmit callback.
  const handleRetrieveSubmit = (): void => {
    if (!browseFlowRef.current) {
      return;
    }
    setIsRetrieveSubmitting(true);
    browseFlowRef.current
      .submit()
      .then((fileIdsFromRetrieve) => {
        onSelectFiles(fileIdsFromRetrieve);
        onRequestClose();
      })
      .catch((error: unknown) => {
        if (error instanceof Error) {
          notifications.error(error.message);
        } else {
          throw error;
        }
      })
      .finally(() => {
        setIsRetrieveSubmitting(false);
      });
  };

  const hasMaxItems = maxItems != null && selectedFileIds.length >= maxItems;
  const maxFilesWarningBanner = (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      supportingInfo={`Maximum file limit of ${maxItems} has been reached. Files must be removed before any new files can be added.`}
    />
  );

  return (
    <Modal
      // The select from connection UI is really cramped unless we expand the modal
      large={activeTab === TabType.CONNECTION || activeTab === TabType.RETRIEVE}
      enableRedesign={true}
      title={title}
      onRequestClose={onRequestClose}
      onRequestSubmit={activeTab === TabType.RETRIEVE ? null : handleSubmit}
      buttons={[
        {text: 'Cancel', cancel: true},
        activeTab === TabType.RETRIEVE
          ? {
              text: isRetrieveSubmitting ? 'Retrieving...' : 'Select',
              style: ButtonStyle.PRIMARY,
              onClick: handleRetrieveSubmit,
              disabled: isRetrieveSubmitting,
            }
          : {
              text: 'Select',
              submit: true,
            },
      ]}
    >
      <TabContainer<TabType> activeTab={activeTab} onRequestTabChange={setActiveTab}>
        <Tab tabKey={TabType.CONNECTION} title="Select from connection">
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <ConnectionFilesTab
              {...props}
              selectedFileIds={selectedFileIds}
              setSelectedFileIds={setSelectedFileIds}
            />
          </div>
        </Tab>
        <Tab tabKey={TabType.DATA_CATALOG} title="Select from data catalog">
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <SearchInput
              autoFocus={false}
              disableTyping={hasMaxItems || (!isMulti && selectedFileIds.length === 1)}
              maxItems={maxItems}
              types={[ModelTypeConstants.FILE]}
              selected={selectedFileIds}
              onSelect={(fileId) => {
                if (fileId == null) {
                  return;
                }
                if (isMulti) {
                  // Do nothing if the number of selected files is already >= maxItems, and log an error so
                  // that we know that something is wrong with our UI.
                  if (hasMaxItems) {
                    logging.error(
                      'User was able to select files past maxItems limit from "Select from data catalog" tab.'
                    );
                    return;
                  }
                  setSelectedFileIds([...selectedFileIds, fileId]);
                } else {
                  setSelectedFileIds([fileId]);
                }
              }}
              onRemove={(fileId) => {
                if (fileId == null) {
                  return;
                }
                setSelectedFileIds(selectedFileIds.filter((id) => id !== fileId));
              }}
              placeholder={hasMaxItems ? '' : 'Search for files...'}
              isMulti={isMulti}
            />
          </div>
        </Tab>
        <Tab tabKey={TabType.UPLOAD} title={`Upload file${isMulti ? 's' : ''}`}>
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <FilesPickerComponent
              isDisabled={hasMaxItems}
              multiple={isMulti}
              // Calculate the max files that a user can select for upload based on how many files have already
              // been selected.
              maxFiles={maxItems == null ? undefined : maxItems - selectedFileIds.length}
              upload={(files: Array<File>) => {
                // Do nothing if the number of selected files is already >= maxItems, and log an error so
                // that we know that something is wrong with our UI.
                if (hasMaxItems) {
                  logging.error('User was able to select files past maxItems limit from "Upload files" tab.');
                  return $.when();
                }
                return onUploadFiles(
                  files,
                  // If isMulti=true, then the new file(s) will be appended to the list of already selected
                  // files. Otherwise, the existing file will be overwritten by the new file.
                  isMulti ? selectedFileIds : []
                );
              }}
              bulkUpload={true}
              ref={fileInputRef}
            />
          </div>
        </Tab>
        <Tab tabKey={TabType.RETRIEVE} title="Retrieve data from connection">
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <p className={css(styles.retrieveDescription)}>
              Retrieve files from connections that do not automatically synchronize files. Select a connection
              to retrieve from.
            </p>
            <RetrieveFromConnectionTab runId={runId} isMulti={isMulti} browseFlowRef={browseFlowRef} />
          </div>
        </Tab>
      </TabContainer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  tabBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  connectionTabBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  browseFilesFlow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  retrieveDescription: {
    ...FONT_PROXIMA_13,
    color: GRAY_SEMIDARK,
    margin: 0,
  },
  sectionHeader: {
    marginTop: 16,
    marginBottom: 4,
    fontWeight: 600,
  },
});
